<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useInstallationName } from 'shared/mixins/globalConfigMixin';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;
const globalConfig = getters['globalConfig/get'];

const { t } = useI18n();

const integrationStatus = computed(() =>
  props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED')
);

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);
</script>

<template>
  <div
    class="flex flex-col flex-1 p-6 bg-white border border-solid rounded-md dark:bg-slate-800 border-slate-50 dark:border-slate-700/50 hover:border-n-brand dark:hover:border-n-brand transition-all"
  >
    <!-- Header with logo, name, and status -->
    <div class="flex items-center mb-4">
      <div class="flex h-12 w-12 mr-3">
        <img
          :src="`/dashboard/images/integrations/${id}.png`"
          class="max-w-full rounded-md border border-slate-50 dark:border-slate-700/50 shadow-sm bg-white dark:bg-slate-900"
        />
      </div>
      <div class="flex items-center">
        <span class="text-base font-semibold text-slate-800 dark:text-slate-100">{{ name }}</span>
        <div
          class="inline-flex items-center py-1 px-2 leading-3 text-xs ml-2 rounded-full"
          :class="enabled 
            ? 'bg-woot-500 dark:bg-woot-700 text-white' 
            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'"
        >
          <i v-if="enabled" class="i-lucide-circle-check w-3 h-3 mr-1"></i>
          <i v-else class="i-lucide-circle-dashed w-3 h-3 mr-1"></i>
          {{ integrationStatus }}
        </div>
      </div>
    </div>
    
    <!-- Description -->
    <div class="flex-1 mb-4">
      <p class="text-slate-700 dark:text-slate-200">
        {{ useInstallationName(description, globalConfig.installationName) }}
      </p>
    </div>
    
    <!-- Config button -->
    <div class="flex justify-end">
      <router-link :to="actionURL">
        <div class="rounded-lg button-group outline-1 outline outline-n-container">
          <button
            class="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-transparent border-0 rounded-lg hover:bg-n-alpha-2 transition-colors duration-150 ease-in-out"
          >
            {{ $t('INTEGRATION_APPS.CONFIGURE') }}
          </button>
        </div>
      </router-link>
    </div>
  </div>
</template>
